import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Trash2, UsersRound, X } from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";
import { notify } from "../lib/toast";

function ContactModal({ open, onClose, onSave, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: "",
    type: "customer",
    tax_id: "",
    email: "",
    phone: "",
    balance: 0,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          name: initial.name || "",
          type: initial.type || "customer",
          tax_id: initial.tax_id || "",
          email: initial.email || "",
          phone: initial.phone || "",
          balance: initial.balance ?? 0,
          notes: initial.notes || "",
        });
      } else {
        setForm({ name: "", type: "customer", tax_id: "", email: "", phone: "", balance: 0, notes: "" });
      }
      setError("");
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    if (!form.name.trim()) return setError("İsim gerekli");
    setBusy(true);
    try {
      await onSave({ ...form, balance: Number(form.balance) || 0 });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="contact-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isEdit ? "DÜZENLE" : "YENİ CARİ"}</span>
            <h2>{isEdit ? "Cariyi düzenle" : "Cari ekle"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-contact-modal">
            <X size={18} />
          </button>
        </div>
        <div className="segmented">
          <button
            className={form.type === "customer" ? "active" : ""}
            onClick={() => setForm({ ...form, type: "customer" })}
            data-testid="contact-type-customer"
          >
            Müşteri
          </button>
          <button
            className={form.type === "supplier" ? "active" : ""}
            onClick={() => setForm({ ...form, type: "supplier" })}
            data-testid="contact-type-supplier"
          >
            Tedarikçi
          </button>
        </div>
        <label>
          İsim / Ünvan
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="contact-name-input"
          />
        </label>
        <div className="form-grid">
          <label>
            Vergi/TCKN
            <input
              value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              data-testid="contact-taxid-input"
            />
          </label>
          <label>
            Bakiye (₺)
            <input
              type="number"
              value={form.balance}
              onChange={(e) => setForm({ ...form, balance: e.target.value })}
              data-testid="contact-balance-input"
            />
          </label>
        </div>
        <div className="form-grid">
          <label>
            E-posta
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-email-input"
            />
          </label>
          <label>
            Telefon
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="contact-phone-input"
            />
          </label>
        </div>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-contact-button">
          <Plus size={17} /> {busy ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Cariyi kaydet"}
        </button>
      </div>
    </div>
  );
}

export default function ContactsPage() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contacts");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((c) => {
    if (filter !== "all" && c.type !== filter) return false;
    if (q && !`${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const receivable = items.filter((c) => c.type === "customer").reduce((s, c) => s + (c.balance || 0), 0);
  const payable = items.filter((c) => c.type === "supplier").reduce((s, c) => s + (c.balance || 0), 0);

  const onDelete = async (item) => {
    if (!window.confirm(`${item.name} silinsin mi?`)) return;
    try {
      await api.delete(`/contacts/${item.id}`);
      notify.success("Cari silindi");
      load();
    } catch {
      notify.error("Cari silinemedi");
    }
  };

  const onSave = async (payload) => {
    if (editing) {
      await api.patch(`/contacts/${editing.id}`, payload);
      notify.success("Cari güncellendi");
    } else {
      await api.post("/contacts", payload);
      notify.success("Cari eklendi");
    }
    await load();
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">CARİLER</div>
          <h1>Cari Hesaplar</h1>
          <p>Müşteri ve tedarikçilerinizin bakiyeleri tek ekranda.</p>
        </div>
        <div className="head-actions">
          <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-contact-button">
            <Plus size={17} /> Cari ekle
          </button>
        </div>
      </div>

      <div className="account-summary panel" data-testid="contact-summary">
        <div>
          <span>Alacak toplamı</span>
          <strong className="income-text">{money(receivable)}</strong>
        </div>
        <div>
          <span>Borç toplamı</span>
          <strong className="expense-text">{money(payable)}</strong>
        </div>
        <div>
          <span>Net cari</span>
          <strong>{money(receivable - payable)}</strong>
        </div>
      </div>

      <div className="panel inner-page" data-testid="contacts-panel">
        <div className="panel-head filter-head">
          <div className="segmented sm">
            <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")} data-testid="contacts-filter-all">Tümü</button>
            <button className={filter === "customer" ? "active" : ""} onClick={() => setFilter("customer")} data-testid="contacts-filter-customer">Müşteriler</button>
            <button className={filter === "supplier" ? "active" : ""} onClick={() => setFilter("supplier")} data-testid="contacts-filter-supplier">Tedarikçiler</button>
          </div>
          <div className="search-input">
            <Search size={14} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari ara…" data-testid="contacts-search" />
          </div>
        </div>

        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="contacts-empty">
            <UsersRound size={30} />
            <h3>Cari bulunmuyor</h3>
            <p>Müşteri ya da tedarikçi ekleyerek başlayın.</p>
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="empty-add-contact-btn">
              <Plus size={14} /> Cari ekle
            </button>
          </div>
        ) : (
          <div className="contact-list">
            {filtered.map((c) => (
              <div className="contact-row" key={c.id} data-testid={`contact-${c.id}`}>
                <div className={`ct-avatar ${c.type}`}>{c.name[0]?.toUpperCase()}</div>
                <div className="ct-main">
                  <strong>{c.name}</strong>
                  <span>
                    {c.type === "customer" ? "Müşteri" : "Tedarikçi"}
                    {c.tax_id ? ` · ${c.tax_id}` : ""}
                  </span>
                </div>
                <div className="ct-contact">
                  {c.email ? <small>{c.email}</small> : null}
                  {c.phone ? <small>{c.phone}</small> : null}
                </div>
                <div className={`ct-balance ${c.type === "customer" ? "income-text" : "expense-text"}`}>
                  {money(c.balance)}
                </div>
                <button
                  className="icon-btn ghost"
                  onClick={() => { setEditing(c); setModal(true); }}
                  data-testid={`edit-contact-${c.id}`}
                  aria-label="Düzenle"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="icon-btn ghost"
                  onClick={() => onDelete(c)}
                  data-testid={`delete-contact-${c.id}`}
                  aria-label="Sil"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ContactModal open={modal} onClose={closeModal} onSave={onSave} initial={editing} />
    </>
  );
}
